import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SplitLines } from "@/components/Reveal";
import { AnchorButton } from "@/components/Buttons";
import { HeroCollage } from "@/components/HeroCollage";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, -30]);
  const wallY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-5 pb-16 pt-10 sm:px-8 lg:min-h-[calc(100vh-72px)] lg:grid-cols-12 lg:gap-6 lg:px-12 lg:pb-20 lg:pt-16">
        <div className="relative z-10 flex flex-col justify-center lg:col-span-7 lg:pr-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/55"
          >
            <span className="h-px w-8 bg-red" />
            Roczne abonamenty · Luiza Wójtowicz-Waga
          </motion.p>

          <SplitLines
            as="h1"
            lines={["Cała biblioteka.", "Jeden abonament.", "12 miesięcy."]}
            delay={0.2}
            className="font-display text-[44px] font-medium leading-[0.98] tracking-[-0.02em] text-ink sm:text-6xl lg:text-[76px] xl:text-[88px]"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease }}
            className="mt-8 max-w-[52ch] text-base leading-relaxed text-ink/70 sm:text-lg"
          >
            Zamiast kupować kursy, e-booki i narzędzia pojedynczo, otrzymujesz dostęp do całej biblioteki
            dopasowanej do Twojej roli: nauczycielki języka obcego albo właścicielki szkoły językowej.
            Przez cały rok, wtedy, kiedy jej potrzebujesz.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease }}
            className="mt-10 flex flex-col gap-3 sm:flex-row"
          >
            <AnchorButton to="#abonamenty" variant="red" testId="hero-zobacz-abonamenty-button">
              Zobacz abonamenty
            </AnchorButton>
            <AnchorButton to="#co-dostajesz" variant="outline" testId="hero-sprawdz-co-dostajesz-button">
              Sprawdź, co dostajesz
            </AnchorButton>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-14 grid grid-cols-3 gap-6 border-t border-ink/15 pt-6 sm:max-w-[560px]"
          >
            {[
              ["3", "ścieżki wyboru"],
              ["12", "miesięcy dostępu"],
              ["od 699 zł", "za rok"],
            ].map(([v, l]) => (
              <div key={l}>
                <dt className="font-display text-2xl leading-none text-ink sm:text-3xl">{v}</dt>
                <dd className="mt-1.5 text-[12px] uppercase tracking-[0.14em] text-ink/50">{l}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative lg:col-span-5">
          <HeroCollage wallY={wallY} photoY={photoY} />
        </div>
      </div>
    </section>
  );
};
